// 🔧 entry_view.js
// ✅ Purpose: Public-facing detail page for a single event entry
// ✅ Features:
// - Uses event slug context to load the active event
// - Uses entryId from the route
// - Pulls one entry from /events/{eventId}/cars/{entryId}
// - Pulls matching category from /events/{eventId}/categories/{catid}
// - Shows large entry image, entry number, category, description, and owner name
// - Includes a centered Back to All Entries button
// - Fully block-commented for clarity and future devs

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import SeoHead from "../../components/seo_head";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import { getEntryRegistrationLabel } from "../../utils/entry_registration_label";
import { getEntryImageUrl } from "../../utils/demo_entry_assets";
import { getEntryPath, resolveEntrySnapshot } from "../../utils/entry_urls";

const EntryView = () => {

  // ✅ Pull entryId from /:eventslug/entries/:entryId
  const { entryId } = useParams();

  // ✅ Event context gives us the resolved eventId from the URL slug
  const { eventslug, eventId, eventDoc, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  // ✅ Page data state
  const [entry, setEntry] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [awardBadges, setAwardBadges] = useState([]);

  // ✅ Loading / notification state
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  // ✅ Load entry detail
  useEffect(() => {
    const loadEntry = async () => {
      if (!eventId || !entryId) return;

      try {
        console.log("🚗 Loading public entry view:", {
          eventId,
          entryId,
        });

        setLoading(true);
        setStatus(null);

        // ✅ Load entry document
        const entrySnap = await resolveEntrySnapshot(db, eventId, entryId);

        if (!entrySnap) {
          setEntry(null);
          setStatus({
            type: "warning",
            message: "This entry could not be found.",
          });
          return;
        }

        const loadedEntry = {
          id: entrySnap.id,
          ...entrySnap.data(),
        };

        setEntry(loadedEntry);

        // ✅ Load matching category if available
        if (loadedEntry.catid) {
          const categoryRef = doc(
            db,
            "events",
            eventId,
            "categories",
            String(loadedEntry.catid)
          );

          const categorySnap = await getDoc(categoryRef);

          if (categorySnap.exists()) {
            const categoryData = categorySnap.data();

            setCategoryName(
              categoryData.name ||
                categoryData.category ||
                categoryData.title ||
                "Uncategorized"
            );
          } else {
            setCategoryName("Uncategorized");
          }
        } else {
          setCategoryName("Uncategorized");
        }

        // ✅ Load award badges for this entry
        // - Place awards come from entry.awardBadges
        // - Sponsor awards are matched by sponsorAwardWinnerReg against entry.regcnt
        const startingAwardBadges = Array.isArray(loadedEntry.awardBadges)
          ? loadedEntry.awardBadges
          : [];

        const sponsorsSnap = await getDocs(
          collection(db, "events", eventId, "sponsors")
        );

        const sponsorAwardBadges = sponsorsSnap.docs
          .map((sponsorDoc) => ({
            id: sponsorDoc.id,
            ...sponsorDoc.data(),
          }))
          .filter(
            (sponsor) =>
              String(sponsor.sponsorAwardWinnerReg || "").trim() ===
              String(loadedEntry.regcnt || "").trim()
          )
          .map((sponsor) => ({
            type: "sponsor",
            sponsorId: sponsor.id,
            sponsorSlug: sponsor.sponsorSlug || "",
            label:
              sponsor.sponsorAwardTitle ||
              "Sponsor Award",
            sponsorName:
              sponsor.name ||
              "Sponsor",
          }));

        setAwardBadges([
          ...startingAwardBadges,
          ...sponsorAwardBadges,
        ]);

        console.log("✅ Loaded public entry view:", loadedEntry);
      } catch
       (error) {
        console.error("❌ Failed to load public entry view:", error);

        setStatus({
          type: "danger",
          message: "Unable to load this entry right now. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadEntry();
  }, [eventId, entryId]);

  // ✅ Auto-clear status after 20 seconds
  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

    useEffect(() => {
    if (!ready || !eventId || !entry?.id) return;

    trackEvent({
      action: GA_EVENTS.ENTRY_VIEWED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        entry_id: entry.id,
        entry_number: entry.regcnt || "",
        category_name: categoryName || "",
        source_page: window.location.pathname,
      },
    });
  }, [ready, eventId, eventslug, entry?.id, entry?.regcnt, categoryName]);
  
  // =====================================================
  // 🔎 Entry SEO
  // =====================================================

  const breadcrumbs = [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Entries",
      path: `/${eventslug}/entries`,
    },
    {
      name:
        entry?.description ||
        `Entry #${entry?.regcnt || ""}` ||
        "Entry",
      path: getEntryPath(eventslug, entry || { id: entryId }),
    },
  ];

  const entrySchema = {
    "@context": "https://schema.org",
    "@type": "Thing",
    name:
      entry?.description ||
      `Entry #${entry?.regcnt}`,

    image: getEntryImageUrl(entry, eventDoc),
    description: entry?.description,
  };

  const entrySeo = {
    title:
      `${entry?.description || "Event Entry"} | ${
        eventDoc?.name || "Let's Go Vote"
      }`,

    description:
      `View ${
        entry?.description || "this event entry"
      } in ${
        eventDoc?.name || "a Let's Go Vote event"
      }.`,

    path: getEntryPath(eventslug, entry || { id: entryId }),

    image:
      getEntryImageUrl(entry, eventDoc) ||
      eventDoc?.eventLogo ||
      "https://letsgovote.app/images/social-share-default.png",

    noindex: !isEventPublic(eventDoc),
    structuredData: entrySchema,
    breadcrumbs,
  };

  // ✅ Page loading gate
  if (eventLoading || !ready || loading) {
    return <FullPageSpinner message="Loading entry..." />;
  }

  return (
  <>
    <SeoHead {...entrySeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "900px" }}>
        {/* ✅ Status Message */}
        {status && (
          <div className={`alert alert-${status.type} text-center`}>
            {status.message}
          </div>
        )}

        {/* ✅ Missing Entry State */}
        {!entry && (
          <div className="text-center">
            <h1 className="fw-bold mb-3">Entry Not Found</h1>

            <p className="text-muted">
              This entry may have been removed or the link may be incorrect.
            </p>

            <Link
              to={`/${eventslug}/entries`}
              className="btn btn-primary mt-3"
            >
              Back to All Entries
            </Link>
          </div>
        )}

        {/* ✅ Entry Detail */}
        {entry && (
          <div className="card shadow-sm border-0">
            {/* ✅ Large Entry Image */}
            {getEntryImageUrl(entry, eventDoc) ? (
              <img
                src={getEntryImageUrl(entry, eventDoc)}
                alt={entry.description || "Entry"}
                className="card-img-top"
                style={{
                  maxHeight: "520px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                className="bg-light d-flex align-items-center justify-content-center text-muted"
                style={{ height: "320px" }}
              >
                No Image
              </div>
            )}

            <div className="card-body p-4">
              {/* ✅ Event Name */}
              <p className="text-muted mb-2">
                {eventDoc?.name || "Event Entry"}
              </p>

              {/* ✅ Entry Number / Category */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge bg-dark">
                  {getEntryRegistrationLabel(entry)}
                </span>

                {categoryName && (
                  <span className="badge bg-secondary">{categoryName}</span>
                )}

                {awardBadges.map((award, index) => (
                  <div
                    key={`${award.type || "award"}-${index}`}
                    className="border rounded px-3 py-2 bg-warning-subtle"
                  >
                    <div className="fw-bold text-dark">
                      🏆 {award.label || "Award Winner"}
                    </div>

                    {award.type === "sponsor" && award.sponsorName && (
                      <small className="text-muted">
                        Awarded by{" "}
                        <Link
                          to={`/${eventslug}/sponsors/${award.sponsorSlug || award.sponsorId}`}
                          className="text-decoration-none"
                        >
                          {award.sponsorName}
                        </Link>
                      </small>
                    )}

                  </div>
                ))}
              </div>

              {/* ✅ Entry Title */}
              <h2
                className="fw-bold text-dark mb-3"
                style={{
                  fontSize: "1.75rem",
                  lineHeight: "1.3",
                }}
              >
                {entry.description ||
                  [entry.year, entry.model].filter(Boolean).join(" ") ||
                  "Untitled Entry"}
              </h2>

              {/* ✅ Owner Name */}
              {(entry.name || entry.ownerFirst || entry.ownerLast) && (
                <p className="lead text-muted mb-4">
                  {entry.name ||
                    [entry.ownerFirst, entry.ownerLast]
                      .filter(Boolean)
                      .join(" ")}
                </p>
              )}

              {/* ✅ Optional Year / Model Display */}
              {(entry.year || entry.model) && (
                <div className="border rounded p-3 bg-light mb-4">
                  {entry.year && (
                    <p className="mb-1">
                      <strong>Year:</strong> {entry.year}
                    </p>
                  )}

                  {entry.model && (
                    <p className="mb-0">
                      <strong>Model:</strong> {entry.model}
                    </p>
                  )}
                </div>
              )}

              {/* ✅ Back Button */}
              <div className="text-center">
                <Link
                  to={`/${eventslug}/entries`}
                  className="btn btn-primary"
                >
                  Back to All Entries
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  </>
  );
};

export default EntryView;
